/* ═══════════════════════════════════════════════════════════════
   Time & Expense rebuild (Tranche 4) — Expenses tab.

   Clean-sheet rebuild of the Expenses portion of the old
   TimeTrackingPage.tsx god-file. DataTable-based (inherits ultra
   filtering/grouping/saved views free via storageKey), draft-state +
   blur-save on quantity/comment (no per-keystroke saves — the old
   page called ExpenseService.save() on every keystroke), attachments
   lazy per-row (see AttachmentsCell.tsx — no base64 receipts load on
   page open). The old page's duplicate left-sidebar "Expense Reports"
   list is dropped: it showed the exact same rows the DataTable
   already lists (click-to-select was its only behavior, which the
   table's own row affordances cover), so D8 ("DataTable is THE
   table") absorbs it rather than keeping two lists of the same data.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useState } from 'react';
import { DataTable } from '../../components/DataTable';
import { SearchableSelect } from '../../components/SearchableSelect';
import { Button } from '../../components/ui/button';

import { Input } from '../../components/ui/input';
import { StatusBadge } from '../../components/StatusBadge';
import { DraftField } from './DraftField';
import { AttachmentsCell } from './AttachmentsCell';
import { exportExpensesCSV } from './expenseCsv';
import { TeamService } from '../../services/teamService';
import { ProjectService } from '../../services/projectService';
import { AdminService } from '../../services/adminService';
import { ExpenseService } from '../../services/expenseService';
import type { AuthUser } from '../../services/authService';
import { hasPermission } from '../../../shared/permissions';
import type { TeamMember, Project, ExpenseType, Expense } from '../../../shared/types';

function settled<T>(r: PromiseSettledResult<T>, fallback: T): T {
  return r.status === 'fulfilled' ? r.value : fallback;
}

function formatDateISO(d: Date): string {
  return d.toISOString().split('T')[0];
}

export interface ExpensesTabProps {
  toast: (msg: string, isError?: boolean) => void;
  authUser?: AuthUser;
}

export function ExpensesTab({ toast, authUser }: ExpensesTabProps) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');

  // Old page's "isAdmin" gated both the Approve All button AND the
  // unsubmit-window bypass. Approve All now gates strictly on
  // expenses.approve per the T4 contract (the server has required this
  // permission since Tranche 0 anyway — a view_all-only user's Approve
  // All click already 403'd server-side; this just stops showing a
  // button that never worked for them). The unsubmit bypass keeps the
  // exact prior (broader) permission check.
  const canApprove = hasPermission(authUser?.permissions, 'expenses.approve');
  const canBypassUnsubmitWindow =
    hasPermission(authUser?.permissions, 'expenses.view_all') || canApprove;
  const canExport = hasPermission(authUser?.permissions, 'expenses.export');

  useEffect(() => {
    loadBase();
  }, []);
  useEffect(() => {
    const handler = () => loadBase();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);
  useEffect(() => {
    if (selectedMemberId) loadExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMemberId]);

  async function loadBase() {
    try {
      const [memsR, projsR, etsR] = await Promise.allSettled([
        TeamService.list(),
        ProjectService.list(),
        AdminService.expenseTypes.list(),
      ]);
      const mems = settled(memsR, [] as TeamMember[]);
      const projs = settled(projsR, [] as Project[]);
      const ets = settled(etsR, [] as ExpenseType[]);
      const active = mems.filter((m) => m.active);
      setMembers(active);
      setProjects(projs.filter((p) => p.status === 'active'));
      setExpenseTypes(ets);

      const myId = authUser?.teamMemberId || authUser?.id;
      const me = myId ? active.find((m) => m.id === myId) : null;
      const selectedMember = me || active[0];
      if (selectedMember) setSelectedMemberId(selectedMember.id);
    } catch (err: any) {
      toast('Failed to load data: ' + err.message, true);
    }
  }

  async function loadExpenses() {
    if (!selectedMemberId) return;
    try {
      const list = await ExpenseService.list(selectedMemberId);
      setExpenses(list);
    } catch (err: any) {
      toast('Failed to load expenses: ' + err.message, true);
    }
  }

  function canSelfUnsubmit(exp: Expense): boolean {
    if (exp.status !== 'submitted' || !exp.submittedAt) return false;
    const elapsed = Date.now() - new Date(exp.submittedAt).getTime();
    return elapsed < 10 * 60 * 1000;
  }

  async function addExpense() {
    const today = new Date();
    const dateStr = formatDateISO(today);
    const monthStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    const newExp: Expense = {
      id: '',
      teamMemberId: selectedMemberId,
      projectId: projects[0]?.id || undefined,
      expenseTypeId: expenseTypes[0]?.id || '',
      date: dateStr,
      quantity: 0,
      total: 0,
      comment: '',
      month: monthStr,
      status: 'draft',
      createdAt: '',
      updatedAt: '',
    };
    try {
      await ExpenseService.save(newExp);
      await loadExpenses();
    } catch (err: any) {
      toast('Failed to add expense: ' + err.message, true);
    }
  }

  async function saveExpense(exp: Expense) {
    try {
      const et = expenseTypes.find((t) => t.id === exp.expenseTypeId);
      const rate = et?.rate ?? undefined;
      const total = rate != null ? exp.quantity * rate : exp.quantity;
      const month = exp.date ? exp.date.substring(0, 7) : exp.month;
      await ExpenseService.save({ ...exp, rate, total, month });
      await loadExpenses();
    } catch (err: any) {
      toast('Failed to save: ' + err.message, true);
    }
  }

  // Submit / Undo moved to the bulk bar's `extra` (Table Standard R2) — this
  // guard is still the single source of truth for who can undo what.

  async function handleSubmitAll() {
    const draftIds = expenses.filter((e) => e.status === 'draft').map((e) => e.id);
    if (draftIds.length === 0) {
      toast('No draft expenses to submit.', true);
      return;
    }
    try {
      await ExpenseService.updateStatus(draftIds, 'submitted', selectedMemberId);
      toast(`${draftIds.length} expense(s) submitted.`);
      await loadExpenses();
    } catch (err: any) {
      toast('Submit failed: ' + err.message, true);
    }
  }

  async function handleApproveAll() {
    const submittedIds = expenses.filter((e) => e.status === 'submitted').map((e) => e.id);
    if (submittedIds.length === 0) return;
    try {
      await ExpenseService.updateStatus(submittedIds, 'approved', selectedMemberId);
      toast(`${submittedIds.length} expense(s) approved.`);
      await loadExpenses();
    } catch (err: any) {
      toast('Approve failed: ' + err.message, true);
    }
  }

  const grandTotal = expenses.reduce((s, e) => s + e.total, 0);
  const draftCount = expenses.filter((e) => e.status === 'draft').length;
  const submittedCount = expenses.filter((e) => e.status === 'submitted').length;

  return (
    <div style={{ padding: 'var(--space-6)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>Expense Reports</span>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
          {members.find((m) => m.id === selectedMemberId)?.name || ''} &middot; {expenses.length}{' '}
          total
        </span>

        <div
          style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}
        >
          <Button onClick={addExpense} variant="default" size="sm">
            + New Expense
          </Button>
          {canExport && (
            <Button
              onClick={() =>
                exportExpensesCSV({ expenses, projects, expenseTypes, memberId: selectedMemberId })
              }
              variant="outline"
              size="sm"
            >
              Export
            </Button>
          )}
          <Button onClick={() => window.print()} variant="outline" size="sm">
            Print
          </Button>
          {draftCount > 0 && (
            <Button onClick={handleSubmitAll} variant="default" size="sm">
              Submit All ({draftCount})
            </Button>
          )}
          {submittedCount > 0 && canApprove && (
            <Button onClick={handleApproveAll} variant="outline" size="sm">
              Approve All ({submittedCount})
            </Button>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 'var(--space-4)' }}>
        <DataTable<Expense>
          columns={[
            {
              key: 'date',
              label: 'Date',
              width: 110,
              render: (exp) => {
                const isEditable = exp.status === 'draft';
                return (
                  <Input
                    type="date"
                    value={exp.date}
                    disabled={!isEditable}
                    onChange={(e) => saveExpense({ ...exp, date: e.target.value })}
                  />
                );
              },
              getValue: (exp) => exp.date,
            },
            {
              key: 'project',
              label: 'Project',
              width: 180,
              render: (exp) => {
                const isEditable = exp.status === 'draft';
                return (
                  <SearchableSelect
                    value={exp.projectId || ''}
                    options={projects.map((p) => ({
                      value: p.id,
                      label: p.name,
                      hint: p.projectNumber || undefined,
                    }))}
                    onChange={(v) => saveExpense({ ...exp, projectId: v || undefined })}
                    disabled={!isEditable}
                    placeholder="-- none --"
                    searchPlaceholder="Search projects…"
                  />
                );
              },
              getValue: (exp) => {
                const p = projects.find((p) => p.id === exp.projectId);
                return p ? (p.projectNumber ? `${p.projectNumber} - ${p.name}` : p.name) : '';
              },
            },
            {
              key: 'expenseType',
              label: 'Expense Type',
              width: 150,
              render: (exp) => {
                const isEditable = exp.status === 'draft';
                return (
                  <SearchableSelect
                    value={exp.expenseTypeId}
                    options={expenseTypes.map((t) => ({ value: t.id, label: t.name }))}
                    onChange={(v) => saveExpense({ ...exp, expenseTypeId: v })}
                    disabled={!isEditable}
                    searchPlaceholder="Search expense types…"
                    clearable={false}
                  />
                );
              },
              getValue: (exp) => expenseTypes.find((t) => t.id === exp.expenseTypeId)?.name || '',
            },
            {
              key: 'quantity',
              label: 'Qty / Amt',
              width: 90,
              render: (exp) => {
                const isEditable = exp.status === 'draft';
                return (
                  <DraftField
                    type="number"
                    min="0"
                    step="0.01"
                    value={exp.quantity ? String(exp.quantity) : ''}
                    disabled={!isEditable}
                    placeholder="0.00"
                    onCommit={(v) => saveExpense({ ...exp, quantity: parseFloat(v) || 0 })}
                    style={{ textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}
                  />
                );
              },
              getValue: (exp) => exp.quantity || 0,
            },
            {
              key: 'rate',
              label: 'Rate',
              width: 80,
              render: (exp) => {
                const et = expenseTypes.find((t) => t.id === exp.expenseTypeId);
                return (
                  <span
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--tm)',
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    {et?.rate != null ? `$${et.rate.toFixed(2)}` : 'N/A'}
                  </span>
                );
              },
              getValue: (exp) => expenseTypes.find((t) => t.id === exp.expenseTypeId)?.rate ?? 0,
            },
            {
              key: 'total',
              label: 'Total',
              width: 90,
              render: (exp) => (
                <span
                  style={{
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 600,
                    fontFamily: "'JetBrains Mono', monospace",
                    color: exp.total > 0 ? 'var(--tx)' : 'var(--tm)',
                  }}
                >
                  ${exp.total.toFixed(2)}
                </span>
              ),
              getValue: (exp) => exp.total,
            },
            {
              key: 'comment',
              label: 'Comment',
              render: (exp) => {
                const isEditable = exp.status === 'draft';
                return (
                  <DraftField
                    value={exp.comment || ''}
                    disabled={!isEditable}
                    placeholder="Notes…"
                    onCommit={(v) => saveExpense({ ...exp, comment: v })}
                  />
                );
              },
              getValue: (exp) => exp.comment || '',
            },
            {
              key: 'files',
              label: 'Files',
              width: 110,
              sortable: false,
              render: (exp) => (
                <AttachmentsCell
                  expenseId={exp.id}
                  isEditable={exp.status === 'draft'}
                  toast={toast}
                />
              ),
            },
            {
              key: 'status',
              label: 'Status',
              width: 100,
              render: (exp) => <StatusBadge status={exp.status} />,
              getValue: (exp) => exp.status,
            },
          ]}
          data={expenses}
          keyField="id"
          storageKey="expenses-table"
          emptyTitle="No Expenses"
          emptyMessage='No expenses yet. Click "+ New Expense" to get started.'
          permissions={authUser?.permissions}
          bulkActions={{
            delete: {
              // ANY-of: `expenses.delete` is the superpower (delete a
              // SUBMITTED expense); the catalog also says "anyone may delete
              // their OWN draft", which the server grants under
              // `expenses.edit_own` (ec2-api/server.js, isPreRecordRow +
              // hasEditOwnPerm). BASE holds edit_own but not delete, so a
              // single-key gate hid the button from every base user for their
              // own drafts. The handler's draft-only filter still stands.
              permission: ['expenses.delete', 'expenses.edit_own'],
              onDelete: async (items) => {
                const eligible = items.filter((e) => e.status === 'draft');
                for (const e of eligible) await ExpenseService.delete(e.id);
                await loadExpenses();
                toast(
                  eligible.length === items.length
                    ? 'Expense(s) deleted'
                    : `Deleted ${eligible.length} of ${items.length} (only draft expenses can be deleted)`
                );
              },
              confirmTitle: 'Delete these expenses?',
              confirmDescription: (n) =>
                `${n} draft expense${n === 1 ? '' : 's'} and any attached files will be removed.`,
            },
            exportCsv: { permission: 'expenses.export', filename: 'expenses' },
            extra: (items, meta) => {
              const submittable = items.filter((e) => e.status === 'draft');
              const undoable = items.filter(
                (e) => e.status === 'submitted' && (canBypassUnsubmitWindow || canSelfUnsubmit(e))
              );
              return (
                <>
                  {submittable.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        await ExpenseService.updateStatus(
                          submittable.map((e) => e.id),
                          'submitted',
                          selectedMemberId
                        );
                        toast(
                          `Submitted ${submittable.length} expense${submittable.length === 1 ? '' : 's'}`
                        );
                        await loadExpenses();
                        meta?.clear();
                      }}
                    >
                      Submit {submittable.length}
                    </Button>
                  )}
                  {undoable.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        await ExpenseService.updateStatus(
                          undoable.map((e) => e.id),
                          'draft',
                          selectedMemberId
                        );
                        toast(
                          `Undid ${undoable.length} expense${undoable.length === 1 ? '' : 's'}`
                        );
                        await loadExpenses();
                        meta?.clear();
                      }}
                    >
                      Undo {undoable.length}
                    </Button>
                  )}
                </>
              );
            },
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
        {[
          { label: 'TOTAL EXPENSES', value: `$${grandTotal.toFixed(2)}`, color: 'var(--ac)' },
          { label: 'LINE ITEMS', value: `${expenses.length}`, color: undefined },
          {
            label: 'DRAFT',
            value: `${draftCount}`,
            color: draftCount > 0 ? 'var(--dg)' : undefined,
          },
          {
            label: 'SUBMITTED',
            value: `${submittedCount}`,
            color: submittedCount > 0 ? 'var(--color-warning)' : undefined,
          },
        ].map((c) => (
          <div
            key={c.label}
            style={{
              background: 'var(--sf)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--bd)',
              padding: 'var(--space-3) var(--space-4)',
              flex: 1,
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--tm)',
                fontWeight: 600,
                marginBottom: 'var(--space-1)',
              }}
            >
              {c.label}
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 700,
                fontFamily: "'JetBrains Mono', monospace",
                color: c.color || 'var(--tx)',
              }}
            >
              {c.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ExpensesTab;
